import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Admin, LoginForm } from '../types';
import apiClient from '../services/api';

interface AuthContextType {
  admin: Admin | null;
  login: (credentials: LoginForm) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on app start
    const token = localStorage.getItem('token');
    const adminData = localStorage.getItem('admin');
    
    if (token && adminData) {
      try {
        const parsedAdmin = JSON.parse(adminData);
        setAdmin(parsedAdmin);
        apiClient.setToken(token);
      } catch (error) {
        // Invalid stored data, clear it
        localStorage.removeItem('token');
        localStorage.removeItem('admin');
      }
    }
    
    setIsLoading(false);
  }, []);

  const login = async (credentials: LoginForm) => {
    try {
      const response = await apiClient.login(credentials);
      const { admin: adminData, token } = response;
      
      setAdmin(adminData);
      apiClient.setToken(token);
      localStorage.setItem('admin', JSON.stringify(adminData));
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = () => {
    setAdmin(null);
    apiClient.logout();
    localStorage.removeItem('admin');
  };

  const value = {
    admin,
    login,
    logout,
    isAuthenticated: !!admin,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
